/* eslint-disable react-refresh/only-export-components */
import {
  DimeBreadcrumb,
  DimeBreadcrumbEllipsis,
  DimeBreadcrumbItem,
  DimeBreadcrumbLink,
  DimeBreadcrumbList,
  DimeBreadcrumbPage,
  DimeBreadcrumbSeparator,
  DimeSeparator,
} from '@nudool/design-system';
import { createFileRoute, Link } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/breadcrumb')({
  component: BreadcrumbDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-background p-6">
      {children}
    </div>
  );
}

function BreadcrumbDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Breadcrumb</h1>
      <p className="mb-8 text-muted-foreground">
        Displays the path to the current resource. Compose with{' '}
        <code className="font-mono text-sm">DimeBreadcrumbList</code>,{' '}
        <code className="font-mono text-sm">DimeBreadcrumbItem</code>,{' '}
        <code className="font-mono text-sm">DimeBreadcrumbLink</code>, and{' '}
        <code className="font-mono text-sm">DimeBreadcrumbPage</code>. Use{' '}
        <code className="font-mono text-sm">asChild</code> on links for TanStack Router.
      </p>

      <DocsSection title="Basic">
        <Preview>
          <DimeBreadcrumb>
            <DimeBreadcrumbList>
              <DimeBreadcrumbItem>
                <DimeBreadcrumbLink href="#">Home</DimeBreadcrumbLink>
              </DimeBreadcrumbItem>
              <DimeBreadcrumbSeparator />
              <DimeBreadcrumbItem>
                <DimeBreadcrumbLink href="#">Vendors</DimeBreadcrumbLink>
              </DimeBreadcrumbItem>
              <DimeBreadcrumbSeparator />
              <DimeBreadcrumbItem>
                <DimeBreadcrumbPage>Detail</DimeBreadcrumbPage>
              </DimeBreadcrumbItem>
            </DimeBreadcrumbList>
          </DimeBreadcrumb>
        </Preview>
      </DocsSection>

      <DocsSection title="Router link">
        <Preview>
          <DimeBreadcrumb>
            <DimeBreadcrumbList>
              <DimeBreadcrumbItem>
                <DimeBreadcrumbLink asChild>
                  <Link to="/design-system/button">Button</Link>
                </DimeBreadcrumbLink>
              </DimeBreadcrumbItem>
              <DimeBreadcrumbSeparator />
              <DimeBreadcrumbItem>
                <DimeBreadcrumbPage>Breadcrumb</DimeBreadcrumbPage>
              </DimeBreadcrumbItem>
            </DimeBreadcrumbList>
          </DimeBreadcrumb>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">asChild</code> and wrap a{' '}
          <code className="font-mono">Link</code> so routing stays client-side.
        </p>
      </DocsSection>

      <DocsSection title="Collapsed">
        <Preview>
          <DimeBreadcrumb>
            <DimeBreadcrumbList>
              <DimeBreadcrumbItem>
                <DimeBreadcrumbLink href="#">Home</DimeBreadcrumbLink>
              </DimeBreadcrumbItem>
              <DimeBreadcrumbSeparator />
              <DimeBreadcrumbItem>
                <DimeBreadcrumbEllipsis />
              </DimeBreadcrumbItem>
              <DimeBreadcrumbSeparator />
              <DimeBreadcrumbItem>
                <DimeBreadcrumbLink href="#">Components</DimeBreadcrumbLink>
              </DimeBreadcrumbItem>
              <DimeBreadcrumbSeparator />
              <DimeBreadcrumbItem>
                <DimeBreadcrumbPage>Breadcrumb</DimeBreadcrumbPage>
              </DimeBreadcrumbItem>
            </DimeBreadcrumbList>
          </DimeBreadcrumb>
        </Preview>
      </DocsSection>
    </div>
  );
}
